import { Fragment, type ReactNode } from "react";
import { Link } from "react-router";
import { safeRelativePath } from "./suggested-questions";

const INLINE = /\[([^\]\n]+)\]\(([^)\n]*)\)|\*\*([^*\n]+)\*\*/g;

function renderInline(line: string, workspaceId: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let n = 0;
  for (const m of line.matchAll(INLINE)) {
    const at = m.index ?? 0;
    if (at > last) out.push(line.slice(last, at));
    if (m[1] !== undefined) {
      const path = safeRelativePath((m[2] ?? "").trim());
      out.push(
        path ? (
          <Link key={n++} className="underline" to={`/w/${workspaceId}${path}`}>
            {m[1]}
          </Link>
        ) : (
          m[1]
        ),
      );
    } else {
      out.push(<strong key={n++}>{m[3]}</strong>);
    }
    last = at + m[0].length;
  }
  if (last < line.length) out.push(line.slice(last));
  return out;
}

/**
 * OWN-163: minimal, dependency-free renderer for assistant replies: `[label](/path)` workspace
 * links (router `Link`; anything else is plain text), `**bold**`, line breaks and "- " bullets.
 * Everything is a React text node — never `dangerouslySetInnerHTML`.
 */
export function AssistantMessageText({ text, workspaceId }: { text: string; workspaceId: string }) {
  const blocks: { bullets: boolean; lines: string[] }[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const bullet = /^\s*[-*]\s+(.*)$/.exec(raw);
    const last = blocks.at(-1);
    if (bullet) {
      if (last?.bullets) last.lines.push(bullet[1] ?? "");
      else blocks.push({ bullets: true, lines: [bullet[1] ?? ""] });
    } else if (last && !last.bullets) last.lines.push(raw);
    else blocks.push({ bullets: false, lines: [raw] });
  }
  return (
    <div dir="auto" className="flex flex-col gap-1 text-sm" data-testid="assistant-message-text">
      {blocks.map((b, i) =>
        b.bullets ? (
          <ul key={i} className="m-0 list-disc ps-5">
            {b.lines.map((l, j) => (
              <li key={j}>{renderInline(l, workspaceId)}</li>
            ))}
          </ul>
        ) : (
          <p key={i} className="m-0">
            {b.lines.map((l, j) => (
              <Fragment key={j}>
                {j > 0 ? <br /> : null}
                {renderInline(l, workspaceId)}
              </Fragment>
            ))}
          </p>
        ),
      )}
    </div>
  );
}
